import { ReactNode } from 'react';
import classNames from 'classnames';
import logTrailDetailsAddPhotoClicked from '@alltrails/analytics/events/logTrailDetailsAddPhotoClicked';
import logTrailDetailsUploadActivityClicked from '@alltrails/analytics/events/logTrailDetailsUploadActivityClicked';
import logTrailDetailsWriteReviewClicked from '@alltrails/analytics/events/logTrailDetailsWriteReviewClicked';
import { TrailId } from 'types/Trails';
import { modalSignup } from 'utils/modal_funnel_util';
import { addToList, removeFromList } from 'utils/requests/list_requests';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import * as styles from './styles.module.scss';
import ContributeButton from '../ContributeButton';
import TrailUgcBarInvitation from './TrailUgcBarInvitation';
import RatingsBreakdown from '../types/RatingsBreakdown';
import TrailUgcTab from '../types/TrailUgcTab';

export type TrailUgcBarProps = {
  activeTab: TrailUgcTab;
  completedConnectionId?: number;
  itemCount: number;
  loggedInUserId?: boolean;
  trailId?: TrailId;
  ratingsBreakdown?: RatingsBreakdown;
  avgRating: string;
  showContributeButton?: boolean;
  showInvitation?: boolean;
  ugcSortDropDown: ReactNode;
  handleCompletedConnectionChanged?: (completedConnectionId: number | null) => void;
  openTrailFormModal: () => void;
};

const TrailUgcBar = ({
  activeTab,
  completedConnectionId,
  itemCount,
  loggedInUserId,
  ratingsBreakdown,
  avgRating,
  showContributeButton = true,
  showInvitation = true,
  trailId,
  ugcSortDropDown,
  handleCompletedConnectionChanged,
  openTrailFormModal
}: TrailUgcBarProps): JSX.Element => {
  const languageRegionCode = useLanguageRegionCode();

  const handleCompletedAdded = (listItem: any) => {
    handleCompletedConnectionChanged?.(listItem.id);
  };

  const handleCompletedRemoved = () => {
    handleCompletedConnectionChanged?.(null);
  };

  const onAddToCompletedClick = () => {
    if (!loggedInUserId) {
      const returnToUrl = window.location.pathname + window.location.search;
      modalSignup('trail-more-completed', returnToUrl, languageRegionCode);
      return;
    }

    const completedMagicId = 1001;
    if (completedConnectionId) {
      removeFromList(completedMagicId, completedConnectionId, null /* userId - aka me */)
        .then(() => handleCompletedRemoved())
        .catch(err => {
          throw Error(`There was an error removing the trail from your completed list ${err}`);
        });
    } else {
      addToList(completedMagicId, 'trail', trailId, null /* userId - aka me */)
        .then(listItem => handleCompletedAdded(listItem))
        .catch(err => {
          throw Error(`There was an error adding the trail to your completed list ${err}`);
        });
    }
  };

  const onContributeClicked = (tab: TrailUgcTab) => {
    const analyticsAttr = { trail_id: trailId };
    if (trailId) {
      switch (tab) {
        case 'reviews':
          logTrailDetailsWriteReviewClicked(analyticsAttr);
          break;
        case 'photos':
          logTrailDetailsAddPhotoClicked(analyticsAttr);
          break;
        case 'tracks':
          logTrailDetailsUploadActivityClicked(analyticsAttr);
          break;
        case 'completed':
          onAddToCompletedClick();
          return;
        default:
        // do nothing
      }
    }

    if (!loggedInUserId) {
      const returnToUrl = window.location.pathname + window.location.search;
      modalSignup(`trail-contribute-${tab}`, returnToUrl, languageRegionCode);
      return;
    }

    openTrailFormModal();
  };

  return (
    <div className={classNames(styles.headerContainer, { [styles.borderBottom]: activeTab !== 'reviews' })}>
      <div className={classNames(styles.contributeInfo, { [styles.reviewsContainer]: activeTab === 'reviews' })}>
        <TrailUgcBarInvitation
          activeTab={activeTab}
          itemCount={itemCount}
          avgRating={avgRating}
          ratingsBreakdown={ratingsBreakdown}
          showInvitation={showInvitation}
        />
        {showContributeButton && (
          <ContributeButton completedConnectionId={completedConnectionId} contributeType={activeTab} clickHandler={onContributeClicked} />
        )}
      </div>
      {activeTab !== 'reviews' && ugcSortDropDown}
    </div>
  );
};

export default TrailUgcBar;
